import { Paperclip } from "lucide-react";
import { API } from "@/lib/api";
import { fmtDT } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Head = ({ children }) => <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{children}</p>;

export function TaskChecklist({ t, canToggle, onToggle }) {
  if (!(t.checklist || []).length) return null;
  const pct = t.checklist_progress?.total ? (t.checklist_progress.done * 100 / t.checklist_progress.total) : 0;
  return (
    <div data-testid="otd-checklist">
      <Head>Checklist — {t.checklist_progress?.done}/{t.checklist_progress?.total} completed</Head>
      <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden mb-1.5"><div className="h-full bg-signal-green rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
      {t.checklist.map((c) => (
        <label key={c.key} className="flex items-center gap-2 text-xs py-0.5">
          <input type="checkbox" className="w-3.5 h-3.5 accent-primary" checked={!!c.done} disabled={!canToggle} onChange={() => onToggle(c.key)} data-testid={`otd-check-${c.key}`} />
          <span className={c.done ? "line-through text-muted-foreground" : ""}>{c.label}</span>
          {c.done_by && <span className="text-muted-foreground text-[10px]">· {c.done_by}</span>}
        </label>))}
    </div>
  );
}

export function TaskSubtasks({ t, canAdd, title, setTitle, onAdd }) {
  return (
    <div data-testid="otd-subtasks">
      <Head>Subtasks — {t.subtask_progress?.done || 0} of {t.subtask_progress?.total || 0} completed</Head>
      {(t.subtasks || []).map((st) => (
        <p key={st.task_id} className="text-xs py-0.5 flex items-center gap-2" data-testid={`otd-subtask-${st.task_id}`}>
          <span className="metric text-primary">{st.task_id}</span> <span className="flex-1">{st.title}</span>
          <span className="text-muted-foreground text-[10px] uppercase">{st.status.replace(/_/g, " ")}</span>
        </p>))}
      {canAdd && (
        <div className="flex gap-2 mt-1">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New subtask title" data-testid="otd-subtask-input" />
          <Button size="sm" variant="outline" disabled={!title.trim()} onClick={onAdd} data-testid="otd-subtask-add">Add</Button>
        </div>)}
    </div>
  );
}

export function TaskAttachments({ t, onUpload }) {
  return (
    <div>
      <Head>Attachments ({(t.attachments || []).length})</Head>
      {(t.attachments || []).map((a) => (
        <p key={a.path} className="text-xs"><a className="text-primary underline" href={`${API}/files/${a.path}`} target="_blank" rel="noreferrer" data-testid={`otd-att-${a.name}`}>{a.name}</a> <span className="text-muted-foreground">· {a.by}</span></p>))}
      <label className="inline-flex items-center gap-1.5 text-xs text-primary cursor-pointer mt-1" data-testid="otd-upload-label">
        <Paperclip className="w-3.5 h-3.5" /> Upload proof / document
        <input type="file" className="hidden" onChange={onUpload} data-testid="otd-upload" />
      </label>
    </div>
  );
}

export function TaskShare({ t, candidates, shareUser, setShareUser, onShare, onUnshare }) {
  return (
    <div className="border border-border rounded-md p-3 space-y-2" data-testid="otd-share-box">
      <Head>Shared access ({(t.shared_with || []).length})</Head>
      {(t.shared_with || []).map((sw) => (
        <p key={sw.user_id} className="text-xs flex items-center justify-between">
          <span>{sw.name} <span className="text-muted-foreground">· granted by {sw.granted_by}</span></span>
          <button className="text-signal-red text-[11px]" onClick={() => onUnshare(sw.user_id)} data-testid={`otd-unshare-${sw.user_id}`}>Remove</button>
        </p>))}
      <div className="flex gap-2">
        <select className="flex-1 h-9 rounded-md border border-input bg-background px-2 text-xs" value={shareUser} onChange={(e) => setShareUser(e.target.value)} data-testid="otd-share-select">
          <option value="">Share with…</option>
          {candidates.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
        </select>
        <Button size="sm" variant="outline" onClick={onShare} disabled={!shareUser} data-testid="otd-share-submit">Grant access</Button>
      </div>
    </div>
  );
}

export function TaskComments({ t, comment, setComment, onPost }) {
  return (
    <>
      <div>
        <Head>Comments ({(t.comments || []).length})</Head>
        {(t.comments || []).map((c) => (
          <p key={`${c.at}-${c.user_id}`} className="text-xs border-b border-border/40 py-1">{c.text} <span className="text-muted-foreground">— {c.by} · {fmtDT(c.at)}</span></p>))}
        <div className="flex gap-2 mt-1.5">
          <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment" data-testid="otd-comment-input" />
          <Button size="sm" variant="outline" onClick={onPost} data-testid="otd-comment-submit">Post</Button>
        </div>
      </div>
      <div>
        <Head>History</Head>
        {(t.history || []).slice().reverse().map((h, hi) => (
          <p key={`${h.at}-${h.action}-${hi}`} className="text-xs text-muted-foreground py-0.5">{h.action} — {h.by} · {fmtDT(h.at)}</p>))}
      </div>
    </>
  );
}
